/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: pages\BackupRestorePage.tsx
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the maintenance module.
────────────────────────────────────────────────────────────────*/

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../../../hooks/useI18n';
import { api } from '../../../../lib/api';
import { Upload, Database, Server, RefreshCw, Archive, Play, Trash2, Edit, Copy } from 'lucide-react';
import RestoreTab from '../components/RestoreTab';
import StorageSettingsTab from '../components/StorageSettingsTab';
import BackupJobForm from '../components/BackupJobForm';
import { ModuleFormHeader } from '../../../../components/layouts';
import DataGridSimple from '../../../../components/ui/DataGridSimple';
import { useNotifications } from '../../../../contexts/NotificationContext';
import ConfirmationModal from '../../../../components/ui/ConfirmationModal';

type ViewMode = 'list' | 'create' | 'edit' | 'restore' | 'storage';

export default function BackupRestorePage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const [view, setView] = useState<ViewMode>('list');
    const [selectedJob, setSelectedJob] = useState<any>(null);
    const [jobToDelete, setJobToDelete] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);

    // Sync URL params to View state
    useEffect(() => {
        const mode = searchParams.get('mode');
        if (mode === 'create') {
            setView('create');
        } else if (mode === 'edit') {
            if (!selectedJob) {
                // If no job in state (e.g. refresh), fallback to list
                setSearchParams({});
                setView('list');
            } else {
                setView('edit');
            }
        } else if (mode === 'restore') {
            setView('restore');
        } else if (mode === 'storage') {
            setView('storage');
        } else {
            setView('list');
        }
    }, [searchParams, selectedJob, setSearchParams]);

    // Data for List
    const [jobs, setJobs] = useState([]);

    const loadJobs = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/backup-jobs');
            if (res.data.success) {
                setJobs(res.data.data);
            }
        } catch (error) {
            console.error('Failed to load backup jobs', error);
            showError(t('backupRestore:notifications.failedLoad'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (view === 'list') {
            loadJobs();
        }
    }, [view]);

    const handleBackToList = () => {
        setSearchParams({});
        setView('list');
        setSelectedJob(null);
    };

    const handleSaveSuccess = (action?: 'close' | 'new' | 'stay') => {
        showSuccess(t('common.savedSuccessfully'));
        if (action === 'new') {
            setSearchParams({ mode: 'create' });
            setView('create');
            setSelectedJob(null);
        } else if (action === 'stay') {
            // Do nothing
        } else {
            handleBackToList();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleDuplicate = (job: any) => {
        const copy = {
            ...job,
            id: undefined,
            name: `${job.name} (Copy)`,
            createdAt: undefined,
            updatedAt: undefined,
            lastRunAt: undefined,
            lastRunStatus: undefined,
        };
        setSelectedJob(copy);
        setSearchParams({ mode: 'create' });
    };

    const handleDeleteClick = (id: number) => {
        setJobToDelete(id);
    };

    const confirmDelete = async () => {
        if (!jobToDelete) return;
        const id = jobToDelete;
        setJobToDelete(null);
        try {
            await api.delete(`/api/backup-jobs/${id}`);
            showSuccess(t('backupRestore:notifications.deleted'));
            loadJobs();
        } catch (error) {
            showError(t('backupRestore:notifications.failedDelete'));
        }
    };

    const handleRun = async (id: number) => {
        try {
            showSuccess(t('backupRestore:notifications.started'));
            const res = await api.post(`/api/backup-jobs/${id}/run`, {}, { responseType: 'blob' });

            // Check content type to see if it is a blob (file) or JSON response
            const contentType = res.headers['content-type'];
            if (contentType && String(contentType).includes('application/json')) {
                // If backend sends JSON success without file for some reason, handle it??
                // Axios responseType: blob usually forces data to be Blob.
                // We can check size.
            }

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            // const contentDisposition = res.headers['content-type']; 
            const filename = `backup-job-${id}-${new Date().toISOString().slice(0, 10)}.zip`;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);

            showSuccess(t('backupRestore:notifications.completed'));
            loadJobs();
        } catch (error) {
            console.error(error);
            showError(t('backupRestore:notifications.failedRun'));
        }
    };

    const columns = [
        { key: 'name', label: t('backupRestore:columns.name'), visible: true, render: (val: string) => <span className="font-medium">{val}</span> },
        {
            key: 'type', label: t('backupRestore:columns.type'), visible: true, render: (val: string) => (
                <div className="flex items-center gap-2">
                    {val === 'system' ? <Server className="h-4 w-4 text-primary" /> : <Database className="h-4 w-4 text-blue-500" />}
                    <span className="capitalize">{val}</span>
                </div>
            )
        },
        { key: 'storageType', label: t('backupRestore:columns.storage'), visible: true, render: (val: string) => <span className="capitalize">{val}</span> },
        { key: 'lastRunAt', label: t('backupRestore:columns.lastRun'), visible: true, render: (val: string) => val ? new Date(val).toLocaleString() : 'Never' },
        {
            key: 'lastStatus', label: t('backupRestore:columns.status'), visible: true, render: (val: string) => (
                val ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${val === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {val}
                    </span>) : <span className="text-gray-400">-</span>
            )
        },
        {
            key: 'actions',
            label: t('backupRestore:columns.actions'),
            visible: true,
            align: 'right' as const,
            width: '120px',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            render: (_: any, item: any) => (
                <div className="flex items-center justify-end gap-2">
                    <button onClick={() => { setSelectedJob(item); setSearchParams({ mode: 'edit' }); }} title={t('backupRestore:actions.edit')} className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors text-blue-600 dark:text-blue-400">
                        <Edit className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDuplicate(item)} title={t('common.duplicate')} className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors text-emerald-600 dark:text-emerald-400">
                        <Copy className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleRun(item.id)} title={t('backupRestore:actions.run')} className="p-2 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 rounded-md transition-colors text-emerald-600 dark:text-emerald-400">
                        <Play className="h-4 w-4" />
                    </button>
                    <button onClick={() => handleDeleteClick(item.id)} title={t('backupRestore:actions.delete')} className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors text-red-600 dark:text-red-400">
                        <Trash2 className="h-4 w-4" />
                    </button>
                </div>
            )
        }
    ];

    if (view === 'create' || view === 'edit') {
        return (
            <div className="p-6">
                <div className="flex flex-col gap-10">
                    <ModuleFormHeader
                        icon={<Archive className="h-5 w-5 text-primary" />}
                        title={view === 'edit' ? t('backupRestore:actions.edit') : t('backupRestore:actions.add')}
                        backButtonText={t('backupRestore:actions.list')}
                        onBack={handleBackToList}
                        actions={[
                            ...(view === 'edit' ? [{
                                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                                tooltip: t('common.duplicate'),
                                onClick: () => handleDuplicate(selectedJob)
                            }] : [])
                        ]}
                    />
                    <BackupJobForm
                        key={view === 'edit' ? selectedJob?.id : 'create'}
                        onCancel={handleBackToList}
                        onSaveSuccess={handleSaveSuccess}
                        onRun={handleRun}
                        initialData={view === 'edit' ? selectedJob : ((view === 'create' && selectedJob) ? selectedJob : undefined)}
                    />
                </div>
            </div>
        );
    }

    if (view === 'restore') {
        return (
            <div className="p-6">
                <ModuleFormHeader
                    icon={<RefreshCw className="h-5 w-5 text-primary" />}
                    title={t('backupRestore:actions.restore')}
                    backButtonText={t('backupRestore:actions.list')}
                    onBack={handleBackToList}
                />
                <div className="mt-8">
                    {/* Wrap existing component in a card for consistency */}
                    <div className="bg-card border border-border rounded-lg shadow-sm p-6">
                        <RestoreTab />
                    </div>
                </div>
            </div>
        );
    }

    if (view === 'storage') {
        return (
            <div className="p-6">
                <ModuleFormHeader
                    icon={<Archive className="h-5 w-5 text-primary" />}
                    title={t('backupRestore:sections.storageConfig')}
                    backButtonText={t('backupRestore:actions.list')}
                    onBack={handleBackToList}
                />
                <div className="mt-8">
                    <div className="bg-card border border-border rounded-lg shadow-sm p-0">
                        <StorageSettingsTab />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <DataGridSimple
                title={t('backupRestore:title')}
                titleIcon={<Upload className="h-5 w-5 text-primary" />}
                data={jobs}
                columns={columns}
                loading={loading}
                pagination={{ page: 1, limit: 10, total: jobs.length, totalPages: 1 }}
                onPageChange={() => { }}
                onItemsPerPageChange={() => { }}
                searchQuery=""
                onSearch={() => { }}
                sortBy="name"
                sortOrder="asc"
                onSort={() => { }}
                onColumnToggle={() => { }}
                onAdd={() => setSearchParams({ mode: 'create' })}
                addButtonText={t('backupRestore:actions.add')}
                actions={
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSearchParams({ mode: 'restore' })}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-border text-foreground rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                        >
                            <RefreshCw className="h-4 w-4" />
                            {t('backupRestore:actions.restore')}
                        </button>
                    </div>
                }
            />

            <ConfirmationModal
                isOpen={!!jobToDelete}
                onClose={() => setJobToDelete(null)}
                onConfirm={confirmDelete}
                title={t('common.confirmDelete', 'Confirm Deletion')}
                message={t('backupRestore:confirm.delete', 'Are you sure you want to delete this backup job?')}
                confirmText={t('common.delete', 'Delete')}
                cancelText={t('common.cancel', 'Cancel')}
                type="danger"
            />
        </div>
    );
}
